import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  ElementRef,
  ViewChild,
  AfterViewChecked,
} from '@angular/core';

export type ResponseOptionsMenuAction = 'regenerate' | 'crisp' | 'detailed';

/**
 * Shared 3-dots options menu for agent responses (same as chatbot).
 * Options: Regenerate your response, Make the response crisp, Give me a detailed response.
 * Menu opens upward using fixed positioning so it is not clipped by scroll/overflow.
 */
@Component({
  selector: 'app-shared-response-options-menu',
  templateUrl: './response-options-menu.component.html',
  styleUrls: ['./response-options-menu.component.scss'],
})
export class SharedResponseOptionsMenuComponent implements AfterViewChecked {
  @Input() disabled = false;
  /** When false, hide 3-dots menu (per-agent config). Default true. */
  @Input() showRegenerate = true;

  @Input() isAgent: boolean = false;
  /** Timestamp to display in the options menu */
  @Input() timestamp: string = '';
  /** Emitted when user selects an option. Payload: 'regenerate' | 'crisp' | 'detailed' */
  @Output() optionsMenuAction = new EventEmitter<ResponseOptionsMenuAction>();

  @ViewChild('dropdownToggle') dropdownToggle?: ElementRef<HTMLButtonElement>;

  isOpen = false;
  /** Fixed position for dropdown (above button) so it is not clipped by overflow */
  dropdownStyle: { top: string; left: string } = { top: '0', left: '0' };

  private positionDropdownNext = false;

  constructor(private readonly el: ElementRef<HTMLElement>) {}

  toggleMenu(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled) return;
    if (this.isOpen) {
      this.isOpen = false;
      return;
    }
    // Open in next tick so document:click from this same click doesn't close it immediately
    setTimeout(() => {
      this.isOpen = true;
      this.positionDropdownNext = true;
    }, 0);
  }

  ngAfterViewChecked(): void {
    if (!this.positionDropdownNext || !this.isOpen || !this.dropdownToggle?.nativeElement) return;
    this.positionDropdownNext = false;
    const rect = this.dropdownToggle.nativeElement.getBoundingClientRect();
    // 3 items × ~47px + timestamp item if present (~40px) - only for agents
    const menuHeight = (this.timestamp && this.isAgent) ? 182 : 142;
    const gap = 8; // clear space above the button
    this.dropdownStyle = {
      top: `${Math.max(8, rect.top - menuHeight - gap)}px`,
      left: `${rect.left}px`,
    };
  }

  onAction(action: ResponseOptionsMenuAction): void {
    if (this.disabled) return;
    this.isOpen = false;
    this.optionsMenuAction.emit(action);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;
    const target = event.target as Node;
    if (this.el.nativeElement.contains(target)) return;
    this.isOpen = false;
  }
}
